import type { CurrencyCode } from "@/models/currencyCode"
import type { SelectableLanguageCode } from "@/models/i18n"
import type { UnitName } from "@/models/unit"
import { languageToUnitConversionMap } from "@/models/unit"
import { isUndefined, omitBy } from "lodash-es"

import { getCurrencySymbol, getCurrencySymbolPosition } from "./currency"
import { getPercentSymbol } from "./percent"
import { translateUnit } from "./unit"

export const DEFAULT_DECIMAL_PLACEHOLDER = "--"

export interface DecimalFormatOptions extends Intl.NumberFormatOptions {
  precision?: number
}

export interface DecimalOptions {
  currencyCode?: CurrencyCode
  placeholder?: string
  replaceZeroWithPlaceholder?: boolean
  showUnit?: boolean
  unit?: UnitName
  unitsSystem?: string
  useAccountingFormatWhenNegative?: boolean
}

export const defaultDecimalFormatOptions: DecimalFormatOptions = {
  maximumFractionDigits: 20,
}

export const defaultCurrencyFormatOptions: DecimalFormatOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: "currency",
}

/**
 * Whole-number yen for customer on-screen formatters. Apply to already-merged
 * options so JPY wins over caller precision.
 */
export const withYenWholeNumberFormat = (
  format: DecimalFormatOptions
): DecimalFormatOptions => {
  if (format.currency !== "JPY") {
    return format
  }

  const yenFormat: DecimalFormatOptions = {
    ...format,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }
  delete yenFormat.precision

  return yenFormat
}

export const defaultPercentFormatOptions: DecimalFormatOptions = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: "percent",
}

const defaultDecimalOptions: DecimalOptions = {
  placeholder: DEFAULT_DECIMAL_PLACEHOLDER,
  replaceZeroWithPlaceholder: false,
  showUnit: true,
  useAccountingFormatWhenNegative: true,
}

export interface IsDecimalEmptyOptions {
  includeZero?: boolean
}

/**
 * Determines whether a value is "empty" (e.g. null, undefined, NaN) or not
 *
 * @param value - The value
 * @param options - The options
 * @param options.includeZero - Whether to consider 0 as an "empty" value
 * @returns - Whether the value is "empty" or not
 * @example
 * isDecimalEmpty(waterUsage, { includeZero: true })
 */
export const isDecimalEmpty = (
  value: number | string | null | undefined,
  options: IsDecimalEmptyOptions = {}
): boolean => {
  const config: IsDecimalEmptyOptions = {
    includeZero: false,
    ...options,
  }

  const numericValue: number =
    typeof value === "number" ? value : Number.parseFloat(value ?? "")

  return (
    !Number.isFinite(numericValue) ||
    ((config.includeZero ?? false) && numericValue === 0)
  )
}

interface ConvertDecimalToUnitsSystemUnitParams {
  unit: UnitName
  unitsSystem: string
  value: number
}

const convertDecimalToUnitsSystemUnit = ({
  unit,
  unitsSystem,
  value,
}: ConvertDecimalToUnitsSystemUnitParams): number => {
  const conversionMap = languageToUnitConversionMap.get(unitsSystem)

  if (!conversionMap) {
    return value
  }

  const conversionFunction = conversionMap.get(unit)

  if (!conversionFunction) {
    return value
  }

  return conversionFunction(value)
}

const getTranslateDecimalDefaultFormatOptions = (
  formatStyle: string | undefined
): DecimalFormatOptions => {
  if (formatStyle === "currency") {
    return defaultCurrencyFormatOptions
  }

  if (formatStyle === "percent") {
    return defaultPercentFormatOptions
  }

  return defaultDecimalFormatOptions
}

interface GetTranslatedUnitProps {
  localeCode: SelectableLanguageCode
  unitOptions: DecimalOptions
  value: string | number | null | undefined
}

const getTranslatedUnit = ({
  localeCode,
  unitOptions,
  value,
}: GetTranslatedUnitProps): string => {
  if (!unitOptions.unit) {
    return ""
  }

  return ` ${translateUnit({
    currencyCode: unitOptions.currencyCode,
    localeCode,
    unit: unitOptions.unit,
    unitsSystem: unitOptions.unitsSystem ?? "en-US",
    value,
  })}`
}

interface GetTranslatedPlaceholderParams {
  formatOptions: DecimalFormatOptions
  localeCode: SelectableLanguageCode
  options: DecimalOptions
  value: number | null | undefined
}

const getTranslatedPlaceholder = ({
  formatOptions,
  localeCode,
  options,
  value,
}: GetTranslatedPlaceholderParams): string => {
  if (formatOptions.style === "currency") {
    const currencySymbol = getCurrencySymbol(
      localeCode,
      formatOptions.currency as CurrencyCode,
      { includeSpacing: true }
    )
    const currencySymbolPosition = getCurrencySymbolPosition(
      localeCode,
      formatOptions.currency as CurrencyCode
    )

    return currencySymbolPosition === "left"
      ? `${currencySymbol}${options.placeholder}${getTranslatedUnit({ localeCode, unitOptions: options, value })}`
      : `${options.placeholder}${currencySymbol}${getTranslatedUnit({ localeCode, unitOptions: options, value })}`
  }

  if (formatOptions.style === "percent") {
    const percentSymbol = getPercentSymbol(localeCode, {
      includeSpacing: true,
    })

    return `${options.placeholder}${percentSymbol}`
  }

  return `${options.placeholder}${options.showUnit ? getTranslatedUnit({ localeCode, unitOptions: options, value }) : ""}`
}

export interface TranslateDecimalParams {
  format?: DecimalFormatOptions | null
  localeCode: SelectableLanguageCode
  options?: DecimalOptions
  value: number | string | null | undefined
}

/**
 * Translates a decimal value into a localized formatted string.
 * For internal use only. Do not use this function unless you know what you're doing!
 *
 * @param params - The parameters for formatting.
 * @param params.format - The Intl.NumberFormat options to use.
 * @param params.localeCode - The locale code to use for formatting.
 * @param params.options - Additional options for formatting.
 * @param params.value - The value to format.
 * @returns - The localized formatted decimal string.
 */
export const translateDecimal = ({
  format,
  localeCode,
  options,
  value,
}: TranslateDecimalParams): string => {
  const numericValue: number =
    typeof value === "string" ? Number.parseFloat(value) : (value ?? Number.NaN)
  const formatConfig: DecimalFormatOptions = {
    ...getTranslateDecimalDefaultFormatOptions(format?.style),
    ...omitBy(format, isUndefined),
  }
  const optionsConfig: DecimalOptions = {
    ...defaultDecimalOptions,
    ...omitBy(options, isUndefined),
  }

  if (optionsConfig.useAccountingFormatWhenNegative) {
    formatConfig.signDisplay = "never"
  }

  if (
    isDecimalEmpty(numericValue, {
      includeZero: optionsConfig.replaceZeroWithPlaceholder,
    })
  ) {
    return getTranslatedPlaceholder({
      formatOptions: formatConfig,
      localeCode,
      options: optionsConfig,
      value: numericValue,
    })
  }

  if (Number.isFinite(numericValue)) {
    let convertedNumericValue: number = numericValue

    if (optionsConfig.unitsSystem && optionsConfig.unit) {
      convertedNumericValue = convertDecimalToUnitsSystemUnit({
        unit: optionsConfig.unit,
        unitsSystem: optionsConfig.unitsSystem,
        value: numericValue,
      })
    }

    // We need to handle significantDigits before fractionDigits in order to have fractionDigits win out
    // https://stackoverflow.com/questions/55834596/use-intl-numberformat-with-maximumfractiondigits-and-maximumsignificantdigi
    if (
      Number.isInteger(formatConfig.minimumSignificantDigits) ||
      Number.isInteger(formatConfig.maximumSignificantDigits)
    ) {
      // Run through Intl.NumberFormat with hardcoded en-US locale
      // to get significant digits set before applying other internationalization
      const significantified: string = Intl.NumberFormat("en-US", {
        minimumSignificantDigits: formatConfig.minimumSignificantDigits,
        maximumSignificantDigits: formatConfig.maximumSignificantDigits,
      })
        .format(convertedNumericValue as unknown as number)
        .replace(/,/g, "")

      convertedNumericValue = Number(significantified)

      delete formatConfig.minimumSignificantDigits
      delete formatConfig.maximumSignificantDigits
    }

    // Handling of custom "precision" format
    if (Number.isInteger(formatConfig.precision)) {
      formatConfig.minimumFractionDigits = formatConfig.precision
      formatConfig.maximumFractionDigits = formatConfig.precision
    }

    const showAccountingParenthesis: boolean =
      (convertedNumericValue as unknown as number) < 0 &&
      (optionsConfig.useAccountingFormatWhenNegative ?? false)

    return `${showAccountingParenthesis ? "(" : ""}${Intl.NumberFormat(localeCode, formatConfig).format(convertedNumericValue)}${showAccountingParenthesis ? ")" : ""}${optionsConfig.showUnit ? getTranslatedUnit({ localeCode, unitOptions: optionsConfig, value: convertedNumericValue }) : ""}`
  }

  return ""
}
